import { Injectable, Injector } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot } from '@angular/router';
import { ConfigService } from 'src/app/shared/services/config/config.service';
import { CheckinHttpClient } from 'src/app/shared/services/http/http-client.service';
import { Observable, Subject } from 'rxjs';
import { EnvService } from 'src/app/shared/common/services/env.service';
import { sanitizeHash } from 'src/app/shared/utils/sanitize';
import { ErrorHandler } from 'src/app/shared/handlers/error.handler';
import { InjectorService } from 'src/app/shared/services/injector/injector.service';
import { TProject } from '../../models/projects/project.model';
import { AnalyticsService } from 'src/app/shared/services/analytics/analytics.service';
import { EModules } from 'src/app/app.enum';

@Injectable({
  providedIn: 'root'
})
export class BookingGuard implements CanActivate {
  private config: ConfigService;
  private env: EnvService;
  private error: ErrorHandler;
  private http: CheckinHttpClient;
  private analyticsService: AnalyticsService;

  constructor() {
    const injector: Injector = InjectorService.get();

    this.config = injector.get(ConfigService);
    this.env = injector.get(EnvService);
    this.http = injector.get(CheckinHttpClient);
    this.analyticsService = injector.get(AnalyticsService),

    this.error = new ErrorHandler();
  }

  public canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const hash = sanitizeHash(route.paramMap.get('hash'));
    const project: TProject = this.config[this.config.project];

    Object.assign(project, {
      hash,
      fallbackUrl: `${project.baseCheckinUrl}/${hash}`,
      routeFallback: `/${this.config.project}/${hash}`
    });

    const url = `${this.env.endpoint}/v2/config/${this.config.project}/${project.hash}`;
    const result: Subject<boolean> = new Subject<boolean>();

    this.http.get(url).subscribe((checkinConfig: any) => {
      if (!checkinConfig) {
        this.error.handler(new Error(`Checkin config is required: ${JSON.stringify(checkinConfig, null, 2)}`));

        result.next(false);
        return;
      }

      this.config.update(checkinConfig, () => result.next(true));
      if (this.config.project === EModules.CHECKIN) {
        this.analyticsService.openCheckin(this.config.project);
      }
    }, (err: Error) => this.error.handler(err).subscribe((r) => result.next(r)));

    return result.asObservable();
  }
}
